import Dexie, { type Table } from 'dexie';
import type { GameStatus } from '@familyhub/shared';
import { registerCleanup } from '../platform/cleanup';
import type { OutboxEntry, MetaRow, RejectionRow } from '../platform/sync-runner';

/**
 * The Wordle module's local database — a **separate** IndexedDB database from
 * every other module (`familyhub-wordle`), which is the module-independence
 * boundary on the client. Its table, outbox and cursor never touch another
 * module's store.
 *
 * Rows are stored exactly as the server sends them, tombstones (`deleted_at`)
 * included and filtered at read time. Only the answer and the guesses are
 * stored; the tile grid is recomputed from them by `restoreGame`.
 */

export interface WordleGameRecord {
  id: string;
  puzzle_date: string;
  target_word: string;
  guesses: string[];
  status: GameStatus;
  created_by: string | null;
  created_at?: string;
  updated_at: string;
  deleted_at: string | null;
}

export class WordleDb extends Dexie {
  games!: Table<WordleGameRecord, string>;
  outbox!: Table<OutboxEntry, number>;
  rejections!: Table<RejectionRow, string>;
  meta!: Table<MetaRow, string>;

  constructor() {
    super('familyhub-wordle');
    this.version(1).stores({
      games: 'id, puzzle_date, created_by, status, deleted_at',
      outbox: '++seq, queued_at',
      rejections: 'id, at',
      meta: 'key',
    });
  }
}

export const wdb = new WordleDb();

/** The Dexie tables the sync runner mirrors, keyed by the server's change bucket. */
export const WORDLE_TABLE_MAP = [
  { name: 'wordle_games', table: wdb.games as unknown as Table<Record<string, unknown>, string> },
];

/** Wipe on logout — registered with the platform cleanup registry. */
export async function clearWordleData(): Promise<void> {
  await Promise.all([wdb.games.clear(), wdb.outbox.clear(), wdb.rejections.clear(), wdb.meta.clear()]);
}

registerCleanup(clearWordleData);
